import {
    AllNeedingAssignmentQuery,
    AllNeedingAttentionQuery,
    AllVisitsByStateQuery,
} from '@/pages/AllVisitsPage/AllVisits.gql.gql.types';
import { VisitState, VisitWorkflowStep } from '@/types/graphql';
import { Utils } from '@/types/utils';
import {
    GridColDef,
    GridRenderCellParams,
    GridRenderEditCellParams,
    ValueOptions as GridValueOptions,
    GridValueOptionsParams,
} from '@mui/x-data-grid';

/**
 * Sentinel tab key for the "All" tab — view-only, equivalent to the union of all
 * visit state tabs (i.e. no state filter beyond the page's date range and dialog
 * filters). Defined as a string literal that doesn't collide with any
 * VisitWorkflowStep / VisitState enum value.
 */
export const ALL_VISITS_TAB = 'ALL' as const;

export namespace AllVisitsPageNS {
    export const PRIMARY_TABS = {
        [ALL_VISITS_TAB]: 'All',
        [VisitWorkflowStep.NEEDS_SERVICE]: 'Needs Services',
        [VisitWorkflowStep.NEEDS_CLINICIAN]: 'Needs Clinician',
        [VisitWorkflowStep.NEEDS_DATE]: 'Needs Date',
        [VisitState.UNSCHEDULED]: 'Unscheduled',
        [VisitState.SCHEDULED]: 'Scheduled',
        [VisitState.CONFIRMED]: 'Confirmed',
        [VisitState.EN_ROUTE]: 'En Route',
        [VisitState.AT_LOCATION]: 'At Location',
        [VisitState.IN_REVIEW]: 'In Review',
        [VisitState.ENDED]: 'Ended',
    } as const;

    export const WorkflowStepTitles: Record<VisitWorkflowStep, string> = {
        [VisitWorkflowStep.NEEDS_SERVICE]: 'Needs Services',
        [VisitWorkflowStep.NEEDS_CLINICIAN]: 'Needs Clinician',
        [VisitWorkflowStep.NEEDS_DATE]: 'Needs Date',
        [VisitWorkflowStep.NEEDS_TIME]: 'Needs Time',
        [VisitWorkflowStep.SCHEDULED]: 'Scheduled',
        [VisitWorkflowStep.UNKNOWN]: 'Unknown',
    };

    export type PrimaryTab = keyof typeof PRIMARY_TABS;

    export type SecondaryTabConfig = [title: VisitWorkflowStep, isDefaultSelected: boolean];

    export type SecondaryTabConfigSet = Readonly<SecondaryTabConfig>;

    export type TabConfig = {
        tab: PrimaryTab;
        label: string;
        lowerTabs: Set<SecondaryTabConfigSet> | null;
    };

    export type AllNeedingAttentionVisits = NonNullable<
        AllNeedingAttentionQuery['visits']
    >['items'];

    export type AllNeedingAttentionVisitRequests = NonNullable<
        AllNeedingAttentionQuery['visitRequests']
    >['items'];

    type AllNeedingAttentionItem =
        | AllNeedingAttentionVisits[number]
        | AllNeedingAttentionVisitRequests[number];

    export type AllNeedingAttentionItems = AllNeedingAttentionItem[];

    export type AllNeedingAssignmentItems = NonNullable<
        AllNeedingAssignmentQuery['visitRequests']
    >['items'];
    export type AllVisitsByStateItems = NonNullable<AllVisitsByStateQuery['visits']>['items'];

    export type AllVisitsVisit = AllNeedingAttentionVisits | AllVisitsByStateItems;
    export type AllVisitsVisitRequest =
        | AllNeedingAttentionVisitRequests
        | AllNeedingAssignmentItems;

    export type VisitRequestCell = Exclude<AllVisitsVisitRequest[number], Record<string, never>>;
    export type VisitCell = Exclude<AllVisitsVisit[number], Record<string, never>>;

    export type TableData =
        | AllNeedingAttentionItems
        | AllNeedingAssignmentItems
        | AllVisitsByStateItems;

    export type TableRow = TableData[number];
    export type ColDef<T = Utils.UNSAFE_ANY> = GridColDef<AllVisitsPageNS.TableRow, T>;

    export type ColumnDef<T = Utils.UNSAFE_ANY> = {
        def: ColDef<T>;
        tabs: Set<PrimaryTab> | 'all';
    };

    export type RenderEditCell = ColDef['renderEditCell'];
    export type RenderCell = ColDef['renderCell'];
    export type CellClassName = ColDef['cellClassName'];
    export type ValueGetter = ColDef['valueGetter'];
    export type SortComparator<T> = ColDef<T>['sortComparator'];

    export type ValueOptions = (
        params: GridValueOptionsParams<AllVisitsPageNS.TableRow>,
    ) => GridValueOptions[];

    export type CellProps = GridRenderCellParams<TableRow>;
    export type VisitRequestCellProps = GridRenderCellParams<VisitRequestCell>;
    export type VisitCellProps = GridRenderCellParams<VisitCell>;
    export type EditCellProps = GridRenderEditCellParams<TableRow>;
}
